import { MutableRefObject, useCallback, useMemo, useRef, useState } from "react"
import { debounce, Direction } from "@mui/material"
import {
  unstable_getNormalizedScrollLeft as getNormalizedScrollLeft,
  unstable_detectScrollType as detectScrollType,
} from "@mui/utils"

/**
 * useScrollController
 * [@Source](https://github.com/mui/material-ui/blob/v5.11.3/packages/mui-material/src/Tabs/Tabs.js)
 */

export interface IUseScrollToCenterProps {
  vertical?: boolean
  direction?: Direction
  animation?: boolean
  scrollToOptions?: number
}

export type UseScrollToCenter = (props?: IUseScrollToCenterProps) => {
  scrollContainerRef: MutableRefObject<any>
  itemsRefs: MutableRefObject<any>
  displayScroll: { start: boolean; end: boolean }
  addItemRef: (key: string, elem: HTMLElement) => void
  handleContainerScroll: () => void
  scrollIntoItem: (itemRefKey: string) => void
  handleStartScrollClick: () => void
  handleEndScrollClick: () => void
}
export const useScrollToCenter: UseScrollToCenter = ({
  vertical = false,
  direction = "ltr",
  animation = true,
} = {}) => {
  const scrollStart = vertical ? "scrollTop" : "scrollLeft"
  const scrollBehavior = animation ? "smooth" : "auto"
  const start = vertical ? "top" : "left"
  const clientSize = vertical ? "clientHeight" : "clientWidth"
  const scrollContainerRef = useRef(null)
  const itemsRefs = useRef({})
  const isRtl = direction === "rtl"
  const [displayScroll, setDisplayScroll] = useState({
    start: false,
    end: false,
  })

  const scrollIntoItem = useCallback(
    (itemRefKey: string) => {
      const item = itemsRefs.current[itemRefKey]
      const container = scrollContainerRef.current
      if (item && container) {
        if (vertical) {
          const scrollTop =
            item.offsetTop - container.clientHeight / 2 + item.clientHeight / 2
          container.scrollTo({ top: scrollTop, behavior: scrollBehavior })
        } else {
          const scrollLeft =
            item.offsetLeft - container.clientWidth / 2 + item.clientWidth / 2
          container.scrollTo({
            left: isRtl ? -scrollLeft : scrollLeft,
            behavior: scrollBehavior,
          })
        }
      }
    },
    [vertical, scrollBehavior, isRtl]
  )

  const updateScrollButtonState = useCallback(() => {
    const container = scrollContainerRef.current

    if (!container) return

    const { scrollTop, scrollHeight, clientHeight, scrollWidth, clientWidth } =
      container
    let showStartScroll
    let showEndScroll

    if (vertical) {
      showStartScroll = scrollTop > 1
      showEndScroll = scrollTop < scrollHeight - clientHeight - 1
    } else {
      const scrollLeft = getNormalizedScrollLeft(container, direction)
      // use 1 for the potential rounding error with browser zooms.
      showStartScroll = isRtl
        ? scrollLeft < scrollWidth - clientWidth - 1
        : scrollLeft > 1
      showEndScroll = !isRtl
        ? scrollLeft < scrollWidth - clientWidth - 1
        : scrollLeft > 1
    }

    if (
      showStartScroll !== displayScroll.start ||
      showEndScroll !== displayScroll.end
    ) {
      setDisplayScroll({ start: showStartScroll, end: showEndScroll })
    }
  }, [displayScroll, direction, isRtl, vertical])

  const handleContainerScroll = useMemo(
    () =>
      debounce(() => {
        updateScrollButtonState()
      }),
    [updateScrollButtonState]
  )

  const getScrollSize = useCallback(() => {
    let totalSize = 0
    const container = scrollContainerRef.current

    if (!container) return totalSize

    const containerSize = container[clientSize]
    const children = Array.from(container.children)

    for (let i = 0; i < children.length; i += 1) {
      const tab = children[i]
      if (totalSize + tab[clientSize] > containerSize) {
        // If the first item is longer than the container size, then only scroll
        // by the container size.
        if (i === 0) {
          totalSize = containerSize
        }
        break
      }
      totalSize += tab[clientSize]
    }

    return totalSize
  }, [clientSize])

  const moveTabsScroll = useCallback(
    (delta) => {
      const container = scrollContainerRef.current

      if (!container) return

      let scrollValue = container[scrollStart]

      if (vertical) {
        scrollValue += delta
      } else {
        scrollValue += delta * (isRtl ? -1 : 1)
        // Fix for Edge
        scrollValue *= isRtl && detectScrollType() === "reverse" ? -1 : 1
      }

      container.scrollTo({ [start]: scrollValue, behavior: scrollBehavior })
    },
    [isRtl, scrollStart, scrollBehavior, start, vertical]
  )

  const handleStartScrollClick = useCallback(() => {
    moveTabsScroll(-1 * getScrollSize())
  }, [getScrollSize, moveTabsScroll])

  const handleEndScrollClick = useCallback(() => {
    moveTabsScroll(getScrollSize())
  }, [getScrollSize, moveTabsScroll])

  const addItemRef = useCallback((key: string, elem: HTMLElement) => {
    itemsRefs.current[key] = elem
  }, [])

  return {
    displayScroll,
    scrollContainerRef,
    itemsRefs,
    handleContainerScroll,
    addItemRef,
    scrollIntoItem,
    handleStartScrollClick,
    handleEndScrollClick,
  }
}
